var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { Spin } from "../Spin/Spin";
import "./Player.scss";
import { EveVideoPlayer } from "@adm-media/evejs-react-player";
import { useSelector } from "react-redux";
import { selectDocumentVisibilityStatus } from "@adm-media/adam-store-plugin";
import { useMutationObservable } from "../../../hooks/useMutationObserver";
import { PLAYER_VALID_TEXT_TRACK_KINDS } from "../../../utils/constants";
// evejs-react-player >= 0.15 already drops a non-array `textTracks` value, entries
// with no usable `src`, and entries whose `kind` is not a real TextTrackKind
// (older builds instead iterated a bare VTT-url string character by character and
// let video.js coerce each to "subtitles", lighting up an empty CC button). We
// still pre-filter for the one thing the player deliberately does not police:
// chapters are a VOD-only feature, so when `allowChapters` is false (LIVE /
// pre-live) the kind:"chapters" track is dropped here and video.js hides the
// control-bar chapters button.
function normalizeTextTracks(textTracks, allowChapters) {
    if (!Array.isArray(textTracks))
        return [];
    var result = [];
    for (var _i = 0, textTracks_1 = textTracks; _i < textTracks_1.length; _i++) {
        var track = textTracks_1[_i];
        if (!track || typeof track !== "object" || typeof track.src !== "string" || track.src.trim() === "")
            continue;
        if (typeof track.kind !== "string" || !PLAYER_VALID_TEXT_TRACK_KINDS.includes(track.kind))
            continue;
        if (!allowChapters && track.kind === "chapters")
            continue;
        result.push({
            kind: track.kind,
            src: track.src,
            srclang: typeof track.srclang === "string" ? track.srclang : undefined,
            label: typeof track.label === "string" ? track.label : undefined,
            default: typeof track.default === "boolean" ? track.default : undefined
        });
    }
    return result;
}
function toPlayerSource(input, allowChapters) {
    var _a;
    var sources = ((_a = input.sources) !== null && _a !== void 0 ? _a : [])
        .filter(function (item) { return !!item.src; })
        .map(function (item) { return ({ src: item.src, type: item.type }); });
    return { sources: sources, poster: input.poster, textTracks: normalizeTextTracks(input.textTracks, allowChapters) };
}
var Player = function (_a) {
    var currentTime = _a.currentTime, source = _a.source, onPlay = _a.onPlay, onPause = _a.onPause, onReadyStateChange = _a.onReadyStateChange, onPlayerReady = _a.onPlayerReady, onTimeUpdate = _a.onTimeUpdate, onAudioTrackChange = _a.onAudioTrackChange, onTextTrackChange = _a.onTextTrackChange, onVolumeChange = _a.onVolumeChange, isFakeLive = _a.isFakeLive, _b = _a.loop, loop = _b === void 0 ? false : _b, _c = _a.widescreen, widescreen = _c === void 0 ? false : _c, _d = _a.hideControls, hideControls = _d === void 0 ? false : _d, _e = _a.autoplay, autoplay = _e === void 0 ? false : _e, _f = _a.muted, muted = _f === void 0 ? true : _f, _g = _a.forceLiveUi, forceLiveUi = _g === void 0 ? false : _g, _h = _a.forcedLayout, forcedLayout = _h === void 0 ? false : _h, additionalUi = _a.additionalUi, hideSpeed = _a.hideSpeed, disableFullScreen = _a.disableFullScreen, poster = _a.poster, _j = _a.allowChapters, allowChapters = _j === void 0 ? true : _j;
    var playerRef = useRef();
    var wrapperRef = useRef();
    var documentVisibilityStatus = useSelector(selectDocumentVisibilityStatus);
    var _k = useState(undefined), playerReference = _k[0], setPlayerReference = _k[1];
    // Root DOM node video.js attaches state classes to (vjs-paused, vjs-user-active, ...).
    // evejs-react-player wraps its internal <video class="video-js"> as a child of the container we
    // hand it, so we look it up one level deeper than playerRef itself.
    var _l = useState(null), playerRootEl = _l[0], setPlayerRootEl = _l[1];
    useEffect(function () {
        var player = new EveVideoPlayer(playerRef.current, {
            autoplay: autoplay,
            muted: muted,
            loop: loop,
            widescreen: widescreen,
            hideControls: hideControls,
            hideSpeed: hideSpeed,
            // Picture-in-Picture stays disabled project-wide (native browser PiP is already
            // off in evejs-react-player; this hides the CSS PiP control-bar button too).
            pip: false
        });
        if (poster)
            player.setPoster(poster);
        if (currentTime !== undefined) {
            player.currentTime = currentTime;
        }
        setPlayerRootEl(playerRef.current.querySelector(".video-js"));
        if (onPlayerReady) {
            onPlayerReady(player);
        }
        player.on("error", function (err) {
            var _a, _b, _c, _d;
            try {
                var src = void 0;
                try {
                    src = (_c = (_b = (_a = player.getSource()) === null || _a === void 0 ? void 0 : _a.sources) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.src;
                }
                catch (_e) { }
                Sentry.captureException(new Error((_d = err === null || err === void 0 ? void 0 : err.message) !== null && _d !== void 0 ? _d : "EveVideoPlayer error"), {
                    extra: {
                        code: err === null || err === void 0 ? void 0 : err.code,
                        source: src
                    }
                });
            }
            catch (_f) { }
        });
        player.on("play", function () { return onPlay && onPlay(player); });
        player.on("readystatechange", function (_a) {
            var readyState = _a.readyState;
            return onReadyStateChange && onReadyStateChange(player, readyState);
        });
        player.on("volumechange", function () { return onVolumeChange && onVolumeChange(player); });
        player.on("timeupdate", function () { return onTimeUpdate && onTimeUpdate(player); });
        setPlayerReference(player);
        return function () {
            player.dispose();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var onFullScreenCallback = useCallback(function (_a) {
        var isFullscreen = _a.isFullscreen;
        if (!isFullscreen || !disableFullScreen)
            return;
        try {
            playerReference === null || playerReference === void 0 ? void 0 : playerReference.exitFullscreen();
        }
        catch (_b) {
            console.warn("Error while exiting fullscreen");
        }
    }, [disableFullScreen, playerReference]);
    useEffect(function () {
        playerReference === null || playerReference === void 0 ? void 0 : playerReference.on("fullscreenchange", onFullScreenCallback);
        return function () { return playerReference === null || playerReference === void 0 ? void 0 : playerReference.off("fullscreenchange", onFullScreenCallback); };
    }, [playerReference, onFullScreenCallback]);
    var onPauseCallback = useCallback(function () {
        if (isFakeLive && documentVisibilityStatus === "visible" && playerReference) {
            if (typeof (playerReference === null || playerReference === void 0 ? void 0 : playerReference.duration) === "number" &&
                typeof (playerReference === null || playerReference === void 0 ? void 0 : playerReference.currentTime) === "number" &&
                !Number.isNaN(playerReference === null || playerReference === void 0 ? void 0 : playerReference.duration) &&
                !Number.isNaN(playerReference === null || playerReference === void 0 ? void 0 : playerReference.currentTime) &&
                (playerReference === null || playerReference === void 0 ? void 0 : playerReference.duration) > (playerReference === null || playerReference === void 0 ? void 0 : playerReference.currentTime)) {
                playerReference === null || playerReference === void 0 ? void 0 : playerReference.play();
            }
        }
        else
            onPause && onPause(playerReference);
    }, [isFakeLive, playerReference, documentVisibilityStatus]);
    useEffect(function () {
        playerReference === null || playerReference === void 0 ? void 0 : playerReference.on("pause", onPauseCallback);
        return function () { return playerReference === null || playerReference === void 0 ? void 0 : playerReference.off("pause", onPauseCallback); };
    }, [playerReference, onPauseCallback]);
    var audioTrackChangeCallback = useCallback(function (_a) {
        var track = _a.track;
        onAudioTrackChange && playerReference && onAudioTrackChange(playerReference, track);
    }, [onAudioTrackChange, playerReference]);
    var ccTrackChangeCallback = useCallback(function () {
        onTextTrackChange && playerReference && onTextTrackChange(playerReference);
    }, [onTextTrackChange, playerReference]);
    useEffect(function () {
        playerReference === null || playerReference === void 0 ? void 0 : playerReference.on("texttrackchange", ccTrackChangeCallback);
        return function () { return playerReference === null || playerReference === void 0 ? void 0 : playerReference.off("texttrackchange", ccTrackChangeCallback); };
    }, [playerReference, ccTrackChangeCallback]);
    useEffect(function () {
        playerReference === null || playerReference === void 0 ? void 0 : playerReference.on("audiotrackchange", audioTrackChangeCallback);
        return function () { return playerReference === null || playerReference === void 0 ? void 0 : playerReference.off("audiotrackchange", audioTrackChangeCallback); };
    }, [playerReference, audioTrackChangeCallback]);
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f;
        if (!source || !playerReference)
            return;
        var nextSource = toPlayerSource(source, allowChapters);
        // evejs-react-player's setSource() forwards straight to video.js's `.src()`, and an empty
        // sources array there raises MEDIA_ERR_SRC_NOT_SUPPORTED ("No compatible source was
        // found for this media"). Card-preview call sites intentionally pass `source={{}}`
        // together with a poster to show a static image with no video — guard against that
        // instead of handing video.js an empty array.
        if (nextSource.sources.length === 0) {
            playerReference.pause();
            return;
        }
        var currentSrc = (_c = (_b = (_a = playerReference.getSource()) === null || _a === void 0 ? void 0 : _a.sources) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.src;
        var nextSrc = (_d = nextSource.sources[0]) === null || _d === void 0 ? void 0 : _d.src;
        var isSwap = !!currentSrc && currentSrc !== nextSrc;
        if (isSwap) {
            // In-place source swaps are the pre-live -> live transition (progressive MP4 -> HLS)
            // and the multiple-source / audio-language switch. On Safari video.js keeps a single
            // tech instance across that swap and its native -> VHS source-handler hand-off races
            // VHS's segment loaders: a live fMP4 media segment gets transmuxed before its
            // EXT-X-MAP init segment is ready, so `segment.map` is undefined and VHS throws
            // ("undefined is not an object (evaluating 'tracks.text')"), leaving the player
            // frozen on the old frame. video.js's own player.reset() tears the tech/media
            // element back to a clean no-source state (without disposing the player, so no
            // teardown race) which turns the next setSource() into a fresh load.
            try {
                (_f = (_e = playerReference.vjs) === null || _e === void 0 ? void 0 : _e.reset) === null || _f === void 0 ? void 0 : _f.call(_e);
            }
            catch (_g) {
                /* older evejs build without a reachable vjs handle — fall through to setSource */
            }
        }
        playerReference.setSource(nextSource);
        // Safari/iOS does not re-honour the autoplay attribute on a programmatic source swap
        // the way Chrome does, so without this the video would sit paused on the previous frame.
        if (autoplay && isSwap) {
            Promise.resolve(playerReference.play()).catch(function () { });
        }
    }, [source, playerReference, allowChapters]);
    useEffect(function () {
        if (poster && playerReference)
            playerReference.setPoster(poster);
    }, [poster, playerReference]);
    var updateTextTracksPositionCallback = useCallback(function () {
        var htmlCollection = document.getElementsByClassName("vjs-text-track-display");
        var areControlsVisible = ((playerRootEl === null || playerRootEl === void 0 ? void 0 : playerRootEl.classList.contains("vjs-user-active")) || (playerRootEl === null || playerRootEl === void 0 ? void 0 : playerRootEl.classList.contains("vjs-paused"))) &&
            (playerRootEl === null || playerRootEl === void 0 ? void 0 : playerRootEl.classList.contains("vjs-has-started"));
        if (areControlsVisible) {
            for (var _i = 0, htmlCollection_1 = htmlCollection; _i < htmlCollection_1.length; _i++) {
                var element = htmlCollection_1[_i];
                element === null || element === void 0 ? void 0 : element.classList.remove("bottom-0");
            }
        }
        else {
            for (var _a = 0, htmlCollection_2 = htmlCollection; _a < htmlCollection_2.length; _a++) {
                var element = htmlCollection_2[_a];
                element === null || element === void 0 ? void 0 : element.classList.add("bottom-0");
            }
        }
    }, [playerRootEl]);
    useMutationObservable(playerRootEl, updateTextTracksPositionCallback);
    // Handle loop
    useEffect(function () {
        if (playerReference) {
            playerReference.loop = loop;
        }
    }, [loop, playerReference]);
    // Handle currentTime
    useEffect(function () {
        if (playerReference && currentTime !== undefined) {
            playerReference.currentTime = currentTime;
        }
    }, [currentTime, playerReference]);
    useEffect(function () {
        // Disable context menu in player
        wrapperRef.current.addEventListener("contextmenu", function (e) {
            e.preventDefault();
            e.stopPropagation();
        }, true);
    }, [wrapperRef]);
    // Listen for programmatically unmute with button (within Wrapper)
    useEffect(function () {
        if (playerReference) {
            playerReference.muted = muted;
        }
    }, [muted, playerReference]);
    return (_jsxs("div", __assign({ ref: wrapperRef, className: classNames("player-wrapper", {
            "force-live": forceLiveUi,
            "player-wrapper--disable-fullscreen": disableFullScreen
        }) }, { children: [forcedLayout && (_jsx("div", __assign({ className: "player-cover-layout" }, { children: _jsx(Spin, __assign({ className: "player-spinner", size: "large", spinning: true }, { children: _jsx("div", {}) })) }))), _jsx("div", { className: "player-container ".concat(widescreen ? "vjsp-widescreen" : "", " ").concat(hideControls ? "hide-controls" : "", " ").concat(hideSpeed ? "hide-speed" : ""), ref: playerRef }), additionalUi] })));
};
export default Player;
